<template>
  <div v-loading="resendActivationLinkInProgress">
    <div
      v-if="atLogin && hasLoginErrors"
      class="alert alert-danger alert-nobg text-left"
    >
      Incorrect login or password
    </div>
    <div
      v-if="atLogin && accountIsInactive"
      class="alert alert-danger alert-nobg text-left"
    >
      <p v-if="!activationMailSent">
        Account is inactive.
        <a
          v-if="canResendActivationLink"
          href
          class="text-yellow-500"
          @click.prevent="resendActivationLink()"
          >Try to resend activation link</a
        >
      </p>
      <p v-if="activationMailSent" class="text-green-500">
        Activation mail has been sent. Please check your inbox (make sure to
        check spam too).
      </p>
    </div>
    <div
      v-show="!atLogin && hasMagicLinkErrors"
      class="alert alert-danger alert-nobg text-left"
    >
      Wrong e-mail address
    </div>
    <p v-if="atLogin" class="mt-6 mb-0 text-center text-base font-semibold">
      Sign in to your account
    </p>
    <p v-if="!atLogin" class="mt-6 mb-0 text-center text-base font-semibold">
      Reset your password
    </p>
    <div :class="{ 'loader-cover': inProgress }" class="formNew space-y-6">
      <div :class="{ 'mb-5': !atLogin }">
        <label class="block text-sm font-medium leading-5 text-gray-300 mb-1"
          >Email address</label
        >
        <input
          autofocus
          v-model="email"
          class="peer flex-1 w-full rounded-lg text-base placeholder-gray-300 border-2 border-transparent focus:border-blue-500 focus:outline-none focus:ring-0 pl-4 pr-4 py-3 bg-[#F3F5F3]"
          v-show="!magicLinkSent && !requestPasswordResetSent"
          name="email"
          placeholder="E-mail"
          type="text"
          data-testid="email"
          @keyup.enter="submit()"
        />
      </div>
      <div v-show="atLogin">
        <label class="block text-sm font-medium leading-5 text-gray-300 mb-1"
          >Password</label
        >
        <input
          v-model="password"
          class="peer flex-1 w-full rounded-lg text-base placeholder-gray-300 border-2 border-transparent focus:border-blue-500 focus:outline-none focus:ring-0 pl-4 pr-4 py-3 bg-[#F3F5F3]"
          name="password"
          placeholder="Password"
          type="password"
          data-testid="password"
          @keyup.enter="submit()"
        />
      </div>
    </div>
    <div v-show="requestPasswordResetSent" class="row animated fadeIn">
      <div class="col-sm-12">
        <p class="text-success text-sm text-center">
          We have sent you an email with password reset instructions.
        </p>
      </div>
    </div>
    <!-- <div v-show="magicLinkSent" class="row animated fadeIn">
      <div class="col-sm-12">
        <p class="text-success">
          We have sent you a link with account access information
        </p>
      </div>
    </div> -->
    <div class="row">
      <div class="col-md-6">
        <div class="checkbox hidden">
          <label>
            <input type="checkbox" />
            <span>Remember me</span>
          </label>
        </div>
      </div>
      <div
        v-show="!magicLinkSent && !requestPasswordResetSent"
        class="col-sm-12 text-right"
      >
        <template v-if="!atLogin">
          <button
            :disabled="inProgress"
            class="hover:no-underline inline-flex items-center px-5 py-2 border-2 font-bold rounded-lg focus:outline-none transition-colors duration-150 border-yellow-500 bg-yellow-300 hover:bg-yellow-400 hover:text-[#D99627] text-[#D99627] hover:border-yellow-600 w-full justify-center mt-1 text-base"
            @click="requestPasswordReset()"
          >
            Reset password
          </button>
          <!-- <p class="text-center text-sm">or</p> -->
        </template>
        <div
          v-if="!magicLinkSent && !requestPasswordResetSent"
          class="text-center pt-5"
          data-cy="forgot-password"
        >
          <router-link
            v-show="atLogin"
            :to="{ name: 'forgotPasswordPage' }"
            class="forgot-password text-muted hidden"
          >
            Forgot your password?
          </router-link>
          <a
            v-show="atLogin"
            class="font-medium text-sm text-yellow-600 hover:text-yellow-500 no-underline"
            href
            @click.prevent="showForgotPasswordForm()"
            >Forgot your password?</a
          >
          <span v-show="!atLogin" class="text-sm">
            Already have an account?
            <a
              class="forgot-password no-underline text-sm text-yellow-600"
              href
              @click.prevent="showLoginForm()"
              >Sign In</a
            >
          </span>
        </div>
        <button
          v-if="atLogin"
          :disabled="inProgress"
          class="hover:no-underline inline-flex items-center mt-6 px-5 py-2 border-2 font-bold rounded-lg focus:outline-none transition-colors duration-150 border-yellow-500 bg-yellow-300 hover:bg-yellow-400 hover:text-[#D99627] text-[#D99627] hover:border-yellow-600 w-full justify-center text-base"
          @click="submit()"
        >
          <span v-if="atLogin">Sign in</span>
          <!-- <span v-if="!atLogin">Send me magic link</span> -->
        </button>
        <!-- <el-popover
          content="Magic link allows you to access platform without having to enter any passwords."
          trigger="hover"
          width="260"
        >
          <div v-show="!atLogin" slot="reference" class="text-xs text-center">
            <p class="magic-link-help">What is magic link?</p>
          </div>
        </el-popover> -->
      </div>
    </div>

    <slot name="footer">
      <div class="splash-box-footer">
        <span class="hidden-xs">Don't have an account? &nbsp;&nbsp;</span>
        <router-link :to="{ name: 'register' }" class="text-yellow">
          Sign up here
        </router-link>
      </div>
    </slot>
  </div>
</template>

<script>
import Auth from 'services/Auth'
import { mapState } from 'vuex'
import router from 'routes'
import useHttpPost from 'states/generics/useHttpPost'
import { ref } from '@vue/composition-api'

const initialErrors = {
  email: [],
  password: [],
  non_field_errors: [],
  tags: [],
}

export default {
  props: {
    next: { type: String, default: null },
  },
  data: function () {
    return {
      atLogin: true,
      loggedIn: false,
      email: '',
      password: '',
      inProgress: false,
      errors: { ...initialErrors },
      magicLinkSent: false,
      requestPasswordResetSent: false,
      hasMagicLinkErrors: false,
    }
  },
  computed: {
    hasLoginErrors: function () {
      return this.errors.email.length || this.errors.password.length
    },
    accountIsInactive: function () {
      return this.errors.non_field_errors.length
    },
    ...mapState({
      afterLoginRoute(state) {
        let route
        if (state.user.defaultRoute && state.user.defaultRoute.name) {
          route = state.user.defaultRoute
        } else {
          route = { name: 'dashboardStartupList' }
        }
        if (route) {
          if (route.name === 'dashboardStartupList') {
            const newAppUrl = router.resolve(route).href
            window.location.href = newAppUrl
          } else {
            router.push(route)
          }
        }
      },
    }),
    canResendActivationLink() {
      return this.errors.tags.includes('CAN_RESEND_ACTIVATION_LINK')
    },
  },
  created() {
    if (this.$route.query.email) {
      this.email = this.$route.query.email
    }
  },
  methods: {
    submit() {
      const self = this
      self.inProgress = true
      if (self.atLogin) {
        self.submitLogin()
      } else {
        self.submitMagicLink()
      }
    },
    async submitLogin() {
      this.errors = initialErrors
      const request = Auth.obtainAuthToken(this.email, this.password)
      try {
        await request
        const user = await this.$store.dispatch('user/getUser')
        const needToAcceptTerms =
          user && user.isOrganizer && !user.accepted_terms_at
        if (needToAcceptTerms) {
          this.redirectToAcceptTerms()
          return
        }
        this.onLoginDone()
        this.inProgress = true // still show spinner before redirect
      } catch (error) {
        console.log(' => error while submitting:', error.response)
        if (error.response) {
          this.errors = Object.assign({}, this.errors, error.response.data)
        }
        this.inProgress = false
      }
    },
    async submitMagicLink() {
      const request = Auth.sendMagicLink(this.email)
      try {
        const response = await request
        this.inProgress = false
        this.magicLinkSent = true
        this.hasMagicLinkErrors = false
      } catch (error) {
        this.errors = Object.assign({}, this.errors, error.response.data)
        this.inProgress = false
        this.hasMagicLinkErrors = true
      }
    },
    async requestPasswordReset() {
      const email = this.email
      this.inProgress = true
      try {
        const response = await Auth.requestPasswordReset({ email })
        this.requestPasswordResetSent = true
      } catch (error) {
        this.errors = Object.assign({}, this.errors, error.response.data)
        this.inProgress = false
        this.hasMagicLinkErrors = true
      }
    },
    onLoginDone() {
      const self = this
      // TODO: for some reason seems like state is undefined if called before
      // user's profile is fetched
      setTimeout(() => {
        if (self.$route.query.next) {
          router.push({ path: self.$route.query.next })
        } else if (self.next) {
          router.push(self.next)
        } else {
          self.$store.dispatch('user/goToDefaultPage')
        }
      }, 500)
    },
    redirectToAcceptTerms() {
      router.push({ name: 'confirm-agreements' })
    },
    showLoginForm() {
      this.atLogin = true
      this.magicLinkSent = false
    },
    showForgotPasswordForm() {
      this.atLogin = false
    },
  },
  setup() {
    const url = '/auth/resend-activation-link'
    const activationMailSent = ref(false)
    const { postInProgress: resendActivationLinkInProgress, post } =
      useHttpPost({ url, sleep: 3000 })
    async function resendActivationLink() {
      const res = await post({ email: this.email })
      console.log(' => got res:', res)
      if (res.status === 200) {
        activationMailSent.value = true
      }
    }

    return {
      resendActivationLinkInProgress,
      resendActivationLink,
      activationMailSent,
    }
  },
}
</script>

<style lang="scss" scoped>
.magic-link-help {
  cursor: pointer;
}
</style>
